<template>
  <modal :name="name" :width="520" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" :clickToClose="false" @before-open="initialize" @before-close="beforeClose" @opened="focusHeading" @closed="$emit('closed')">
    <div class="popup booking-edit-modal" :aria-busy="busy || uploading" @keydown.esc.prevent.stop="close">
      <button class="popup__close" type="button" :aria-label="`Close ${title}`" :disabled="busy || uploading" @click="close" />
      <h2 ref="heading" class="popup__title" tabindex="-1">{{ title }}</h2>
      <div class="popup__body">
        <form novalidate @submit.prevent="submit">
          <fieldset class="booking-edit-modal__fields" :disabled="busy || uploading || saved || uncertain || !permitted">
            <app-form-group v-if="block === 'pnr'" label="PNR" :label-for="`${name}-pnr`" required>
              <app-input :id="`${name}-pnr`" v-model="draft.pnr" :maxlength="100" placeholder="Enter PNR" :error="Boolean(fieldErrors.pnr)" @input="clearError('pnr')" />
              <template #error><div v-if="fieldErrors.pnr">{{ fieldErrors.pnr }}</div></template>
            </app-form-group>
            <template v-if="block === 'time-limits'">
              <p class="booking-edit-modal__hint">Enter at least one deadline: payment or ticketing.</p>
              <app-form-group v-for="limit in deadlines" :key="limit.key" :label="limit.label" :label-for="`${name}-${limit.key}-date`">
                <booking-deadline-input :id="`${name}-${limit.key}`" v-model="draft[limit.key]" :label="limit.label" :disabled="busy || uploading || saved || uncertain || !permitted" :error="Boolean(fieldErrors[limit.key])" @input-valid="clearError(limit.key)" @input-error="invalidDate(limit.key)" />
                <template #error><div v-if="fieldErrors[limit.key]">{{ fieldErrors[limit.key] }}</div></template>
              </app-form-group>
              <p v-if="fieldErrors.deadlines" class="booking-edit-modal__error" role="alert">{{ fieldErrors.deadlines }}</p>
            </template>
            <template v-if="block === 'ticket'">
              <app-form-group label="Number" :label-for="`${name}-number`">
                <app-input :id="`${name}-number`" v-model="draft.ticket_number" :maxlength="100" placeholder="Enter ticket number" :error="Boolean(fieldErrors.ticket_number)" @input="clearError('ticket_number')" />
                <template #error><div v-if="fieldErrors.ticket_number">{{ fieldErrors.ticket_number }}</div></template>
              </app-form-group>
              <app-form-group label="Attachment" required>
                <p v-if="booking.ticket" class="booking-edit-modal__hint">Choose a replacement to change the saved ticket. The saved file cannot be removed.</p>
                <app-uploader :key="`${sessionId}-${uploaderRevision}`" v-model="draft.ticket" :single-file="true" :readonly="busy || uploading || saved || uncertain || !permitted" @uploading="uploading = $event" @onAddedFile="ticketChanged" @upload-error="uploadError">
                  <template #action="{ filePresent }"><div><button v-if="!filePresent" type="button" class="booking-edit-modal__upload dz-message"><i class="pi pi-paperclip" aria-hidden="true" /> Attach the file</button></div></template>
                  <template #remove-icon><i class="pi pi-times" aria-hidden="true" /><span class="booking-edit-modal__sr">{{ booking.ticket ? 'Choose replacement ticket file' : 'Remove selected ticket file' }}</span></template>
                </app-uploader>
                <p v-if="uploading" class="booking-edit-modal__hint" role="status">Uploading ticket…</p>
                <template #error><div v-if="fieldErrors.ticket">{{ fieldErrors.ticket }}</div></template>
              </app-form-group>
            </template>
          </fieldset>
          <p v-if="!permitted" class="booking-edit-modal__error" role="alert">Editing is no longer available for this booking.</p>
          <p v-if="saved" class="booking-edit-modal__hint" role="status">Saved. Reload booking details before continuing.</p>
          <p v-if="uncertain" class="booking-edit-modal__error" role="alert">The save result could not be verified. Reload the booking before continuing.</p>
          <p v-if="error" class="booking-edit-modal__error" role="alert">{{ error }}</p>
          <footer class="booking-edit-modal__footer">
            <app-button theme="dark" :disabled="busy || uploading" @click.native="close">Cancel</app-button>
            <app-button v-if="saved || uncertain" :disabled="busy || uploading" @click.native="reloadBooking">{{ reloading ? 'Updating…' : 'Reload booking' }}</app-button>
            <app-button v-else type="submit" :disabled="!canSubmit">{{ saving ? 'Saving…' : uploading ? 'Uploading…' : 'Save' }}</app-button>
          </footer>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { getFlightsBooking, patchFlightsBookingPnr, patchFlightsBookingTimeLimits, patchFlightsBookingTicket } from '@/http'
import BookingDeadlineInput from '@/components/flights/BookingDeadlineInput.vue'
import { responseError } from './booking-detail'
import { canEditBookingBlock, bookingEditDraft, bookingEditPayload, bookingEditErrors } from './booking-edits'

const patchBlock = { 'pnr': patchFlightsBookingPnr, 'time-limits': patchFlightsBookingTimeLimits, 'ticket': patchFlightsBookingTicket }

export default {
  name: 'BookingEditModal',
  components: { BookingDeadlineInput },
  props: { name: { type: String, required: true }, booking: { type: Object, required: true }, block: { type: String, required: true, validator: value => ['pnr', 'time-limits', 'ticket'].includes(value) } },
  data() {
    return { draft: {}, initial: {}, fieldErrors: {}, dateErrors: {}, error: '', saving: false, reloading: false, uploading: false, saved: false, uncertain: false, sessionId: 0, uploaderRevision: 0, deadlines: [{ key: 'payment_time_limit', label: 'Payment · PTL' }, { key: 'ticketing_time_limit', label: 'Ticketing · TTL' }] }
  },
  computed: {
    title() { return { 'pnr': 'Edit PNR', 'time-limits': 'Edit time limits', 'ticket': 'Edit ticket' }[this.block] },
    permitted() { return canEditBookingBlock(this.booking, this.$store.state.userGroups, this.block) },
    busy() { return this.saving || this.reloading },
    canSubmit() { return this.permitted && !this.busy && !this.uploading && !this.saved && !this.uncertain && (Object.keys(bookingEditPayload(this.block, this.draft, this.initial)).length > 0 || Object.keys(this.dateErrors).length > 0) }
  },
  beforeDestroy() { this.sessionId++ },
  methods: {
    initialize() {
      this.sessionId++
      this.initial = bookingEditDraft(this.booking)
      this.draft = bookingEditDraft(this.booking)
      this.fieldErrors = {}
      this.dateErrors = {}
      this.error = ''
      this.saving = false
      this.reloading = false
      this.uploading = false
      this.saved = false
      this.uncertain = false
    },
    clearError(field) {
      this.$delete(this.fieldErrors, field)
      this.$delete(this.dateErrors, field)
      if (field.endsWith('_time_limit')) this.$delete(this.fieldErrors, 'deadlines')
    },
    invalidDate(field) {
      const message = 'Enter a valid date and time.'
      this.$set(this.dateErrors, field, message)
      this.$delete(this.fieldErrors, field)
    },
    ticketChanged(file) { this.clearError('ticket'); if (!file || !Object.keys(file).length) this.uploaderRevision++ },
    uploadError(message) { this.$set(this.fieldErrors, 'ticket', message) },
    async submit() {
      if (!this.canSubmit) return
      this.fieldErrors = { ...bookingEditErrors(this.block, this.draft, this.booking), ...this.dateErrors }
      if (Object.keys(this.fieldErrors).length) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      this.saving = true
      this.error = ''
      try {
        const { data } = await patchBlock[this.block](bookingId, bookingEditPayload(this.block, this.draft, this.initial), { localErrors: true })
        if (sessionId !== this.sessionId) return
        this.saved = true
        this.$notify({ type: 'success', title: `${this.title.replace('Edit ', '')} saved.` })
        this.saving = false
        if (data && data.id === bookingId && Array.isArray(data.available_actions)) {
          this.$emit('updated', data)
          this.close()
        } else await this.reloadBooking()
      } catch (error) {
        if (sessionId === this.sessionId) { this.error = responseError(error); this.uncertain = !error.response || error.response.status >= 500 }
      } finally { if (sessionId === this.sessionId) this.saving = false }
    },
    async reloadBooking() {
      if (this.busy || this.uploading) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      this.reloading = true
      this.error = ''
      try {
        const { data } = await getFlightsBooking(bookingId, { localErrors: true })
        if (sessionId !== this.sessionId) return
        if (!data || data.id !== bookingId || !Array.isArray(data.available_actions)) throw new Error('Invalid booking response')
        this.$emit('updated', data)
        this.reloading = false
        this.close()
      } catch (error) { if (sessionId === this.sessionId) this.error = responseError(error, 'Unable to reload booking details.') } finally { if (sessionId === this.sessionId) this.reloading = false }
    },
    focusHeading() { if (this.$refs.heading) this.$refs.heading.focus() },
    close() { if (!this.busy && !this.uploading) this.$modal.hide(this.name) },
    beforeClose(event) { if (this.busy || this.uploading) event.cancel(); else this.sessionId++ }
  }
}
</script>

<style lang="scss" scoped>
.booking-edit-modal .popup__title { margin-bottom: 20px; text-align: left; cursor: default; padding-right: 28px; }
.booking-edit-modal__fields { min-width: 0; margin: 0; padding: 0; border: 0; }
.booking-edit-modal__fields:disabled { opacity: .7; }
.booking-edit-modal__hint { margin: 0 0 16px; color: $color-text-second; font-size: 13px; }
.booking-edit-modal__error { color: $color-error; font-size: 13px; overflow-wrap: anywhere; }
.booking-edit-modal__upload { display: inline-flex; align-items: center; gap: 8px; padding: 11px 14px; border: 0; border-radius: 6px; background: $color-theme; color: #fff; font: inherit; font-size: 13px; cursor: pointer; }
.booking-edit-modal__footer { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
.booking-edit-modal__footer .btn { min-width: 100px; }
.booking-edit-modal__sr { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
.booking-edit-modal .popup__close:focus-visible, .booking-edit-modal__upload:focus-visible { outline: 2px solid $color-theme; outline-offset: 3px; }
</style>
